import React from "react";
//#region types
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import InvoiceDetailsView from "./detailsView";
import CreateInvoice from "./createInvoice";
import { useParams } from "react-router-dom";
import { ErrorView } from "components/error";
import {
  LoadingWithLogo,
  PageLayouts,
  RouteBreadCrumb,
  useAPIRequestKeyRef,
  useMountedState,
  useNavigationHistory,
} from "components/common";
import { useInvoiceService, useSelfInvoiceService } from "serviceHooks";
import CreateSelfInvoiceView from "../selfInvoice/createView";
import useInvoicePermissions from "./useInvoicePemissions";
//#endregion

const InvoiceView: React.FC = () => {
  const invoiceService = useInvoiceService();
  const selfInvoiceService = useSelfInvoiceService();

  const [notFound, setNotFound] = React.useState<boolean>();

  const [invoice, setInvoice] = React.useState<WintSuperkollAPIModel.Invoice>();
  const [selfInvoice, setSelfInvoice] = React.useState<WintSuperkollAPIModel.SelfInvoice>();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [key, setKey] = React.useState(0);

  const { getMounted } = useMountedState();
  const { goBack } = useNavigationHistory();

  const urlParams = useParams<EntityIdParams>();
  const invoiceId = Number.parseInt(urlParams.id ?? "0");

  const { apiRequestKeyRef, updateKey } = useAPIRequestKeyRef();

  const { canUseInvoices } = useInvoicePermissions();

  const loadInvoice = (invoiceId: number) => {
    const currentKey = updateKey();
    setLoading(true);
    invoiceService
      .get(invoiceId, currentKey)
      .then((res) => {
        if (getMounted() && res.key === apiRequestKeyRef.current) {
          setInvoice(res);
          setKey(key + 1);
          setNotFound(false);

          if (res.status === WintSuperkollAPIModel.InvoiceStatus.NotSent && res.selfInvoiceId) {
            selfInvoiceService
              .get(res.selfInvoiceId)
              .then((selfInvoice) => {
                setSelfInvoice(selfInvoice);
              })
              .finally(() => setLoading(false));
          } else {
            setLoading(false);
          }
        }
      })
      .catch(() => {
        setLoading(false);
        if (invoice) {
          setTimeout(() => {
            goBack();
          }, 1000);
        } else setNotFound(true);
      });
  };

  React.useEffect(() => {
    if (invoiceId) {
      if (invoice?.id !== invoiceId) {
        setInvoice(undefined);
        loadInvoice(invoiceId);
      }
    } else {
      setInvoice(undefined);
    }
  }, [invoiceId]);

  const reloadInvoice = () => {
    if (invoice) {
      loadInvoice(invoice.id);
    }
  };

  return notFound ? (
    <ErrorView />
  ) : (
    <PageLayouts.EntityFullScreen
      id={invoice?.id ?? 0}
      entityKey={`invoice`}
      navigation={
        <RouteBreadCrumb
          current={invoice === undefined ? "" : invoice.customerName ?? TranslationKeys.Invoice.CreateInvoice}
          showHome
        />
      }
      hasPermissions={canUseInvoices}
    >
      {invoice && !loading ? (
        invoice.status === WintSuperkollAPIModel.InvoiceStatus.NotSent ? (
          invoice.invoiceSource === WintSuperkollAPIModel.InvoiceSource.SelfInvoice ? (
            <CreateSelfInvoiceView invoice={selfInvoice} draftCustomerInvoice={invoice} reloadInvoice={reloadInvoice} />
          ) : (
            <CreateInvoice
              key={`${invoiceId}${key}`}
              invoice={invoice}
              reloadInvoice={reloadInvoice}
              additionalImages={selfInvoice?.images?.map((meta) => ({
                id: meta.id ?? "",
                contentType: meta.contentType ?? "",
                numberOfPages: 1,
                data: meta.content,
              }))}
            />
          )
        ) : (
          <InvoiceDetailsView key={`${invoiceId}${key}`} invoice={invoice} reloadInvoice={reloadInvoice} />
        )
      ) : (
        <LoadingWithLogo />
      )}
    </PageLayouts.EntityFullScreen>
  );
};

export default InvoiceView;
